<template>
    <div class="send-container">
        <div class="send-loader" v-if="draftSendTime && !draftSent" title="Sending...">
            <straight-line-loader />
        </div>

        <md-button
            v-else
            :title="iconTitle"
            :class="[
                'md-just-icon md-round md-primary',
                {
                    'md-warning': newDraftCostAdded,
                    'md-success': draftSendTime && draftSent,
                    'bulk-send-draft-btn': isBulk
                }
            ]"
            @click.stop="handleSendDraftApproval()"
        >
            <md-tooltip v-if="emailToolTip" md-direction="left">
                {{ emailToolTip }}
            </md-tooltip>
            <md-icon>{{ buttonIcon }}</md-icon>
        </md-button>
    </div>
</template>

<script>
import moment from 'moment';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage } from '@/helpers';
import { StraightLineLoader } from '@/components';
import { DATE_TYPES } from '@/utils/constants';

export default {
    name: 'SendDraftInvoiceButton',
    components: {
        StraightLineLoader
    },
    props: {
        invoiceId: {
            type: [Number, String],
            default: () => null
        },
        isBulk: {
            type: Boolean,
            default: false
        },
        bulkTeamMemberInvoiceIds: {
            type: Array,
            default: () => []
        },
        lastDraftSentTime: {
            type: String,
            default: null
        },
        draftHasBeenSent: {
            type: Boolean,
            default: false
        },
        newDraftCostAdded: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            draftSendTime: this.lastDraftSentTime,
            draftSent: this.draftHasBeenSent,
            unreadIcon: 'mark_email_unread',
            doneIcon: 'done',
            emailIcon: 'email'
        };
    },
    watch: {
        lastDraftSentTime() {
            if (this.draftSendTime) 
                this.draftSent = true;

            this.draftSendTime = this.lastDraftSentTime;
        },
        draftHasBeenSent() {
            this.draftSent = this.draftHasBeenSent;
        }
    },
    mixins: [GeneralMixin],
    computed: {
        buttonIcon() {
            if (this.newDraftCostAdded && this.draftSent) {
                return this.unreadIcon;
            }

            if (this.draftSendTime) {
                return this.doneIcon;
            }

            return this.emailIcon;
        },
        iconTitle() {
            if (this.newDraftCostAdded) {
                return 'New cost added';
            }

            if (this.draftSent) {
                return `Email Sent`;
            }

            return 'Send Email';
        },
        emailToolTip() {
            if (this.draftSent || this.newDraftCostAdded) {
                const emailSent = moment(this.draftSendTime).format(DATE_TYPES.standardDateTime);
                const sendAgain = this.newDraftCostAdded ? 'Re-Send updated invoice.' : '';
                return `${sendAgain} Last email Sent at ${emailSent}`;
            }

            return null;
        }
    },
    methods: {
        handleSendDraftApproval() {
            let message = 'Are you sure you want to send draft approvals?';

            if (this.isBulk) {
                message = 'Are you sure you want to send these invoices?';
            }

            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Email Invoice',
                    body: message,
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        this.draftSendTime = null;
                        this.draftSent = false;

                        if (this.isBulk) {
                            await this.handleBulkSendDraft();
                        } else {
                            await this.handleSendDraft();
                        }
                    }
                });
        },
        async handleBulkSendDraft() {
            const payload = {
                method: 'post',
                data: this.bulkTeamMemberInvoiceIds
            };

            const api = `/api/team-member-invoices/bulk/send-draft`;

            try {
                this.$emit('bulkDraftInvoicesSent', this.bulkTeamMemberInvoiceIds);
                await handleRequests(api, payload);
            } catch (e) {
                const message = 'Error in sending draft payment summaries';
                showErrorMessage(this, message, e);
            }
        },
        async handleSendDraft() {
            if (this.invoiceId == null) {
                showErrorMessage(this, 'No invoice selected');
                return;
            }

            const payload = {
                method: 'post'
            };

            this.draftSendTime = moment().format(DATE_TYPES.standardDateTime);
            const api = `/api/team-member-invoices/${this.invoiceId}/send-draft`;

            try {
                await handleRequests(api, payload);
                this.draftSendTime = moment().format(DATE_TYPES.standardDateTime);
            } catch (e) {
                const message = 'Error in sending draft payment summaries';
                showErrorMessage(this, message, e);
                this.draftSendTime = null;
                this.draftSent = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.send-loader {
    display: inline-block;
    background-color: #2b93ff;
    border-radius: 50%;
    width: 27px;
    height: 27px;
    svg {
        margin-top: 3px;
        margin-left: 2px;
    }
    margin-left: 1px;
    margin-right: 1px;
}

.send-container {
    display: inline-block;
    margin-left: 2px;
}

.bulk-send-draft-btn {
    height: 30px !important;
    width: 30px !important;
    min-width: 30px !important;
    margin: 6px 2px 5px 0px;
}
</style>
